import React from "react";
import { safeUrlFn } from "./Functions";
import { MenuItem, PageContent } from "../../../Core/Content/Types";
export function TextFieldPiece({
  label,
  value,
  onChange,
  multiline = false,
  id,
  error,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  id?: string;
  error?: string;
}) {
  return (
    <label className="cms-field" htmlFor={id}>
      <span>{label}</span>
      {multiline ? (
        <textarea
          id={id}
          aria-invalid={!!error}
          aria-describedby={error && id ? `${id}-error` : undefined}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
          rows={3}
        />
      ) : (
        <input
          id={id}
          aria-invalid={!!error}
          aria-describedby={error && id ? `${id}-error` : undefined}
          value={value || ""}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {error && (
        <small className="cms-field-error" id={id ? `${id}-error` : undefined}>
          {error}
        </small>
      )}
    </label>
  );
}
export function MediaFieldPiece({
  label,
  value,
  kind = "image",
  uploading,
  onUpload,
  onRemove,
}: {
  label: string;
  value: string;
  kind?: "image" | "video";
  uploading: boolean;
  onUpload: (file: File) => void;
  onRemove: () => void;
}) {
  const [error, setError] = React.useState("");
  const inputId = React.useId();
  const types =
    kind === "video"
      ? ["video/mp4", "video/webm"]
      : ["image/png", "image/jpeg", "image/webp"];
  return (
    <section className="cms-media-field" aria-label={label}>
      <h3>{label}</h3>
      <div className="cms-media-preview">
        {value ? (
          kind === "video" ? (
            <video
              key={value}
              src={value}
              controls
              preload="metadata"
              playsInline
            />
          ) : (
            <img src={value} alt={label} />
          )
        ) : (
          <p>No {kind === "video" ? "video" : "photo"} selected</p>
        )}
      </div>
      <div className="cms-actions">
        <label
          className={`cms-media-upload ${uploading ? "is-disabled" : ""}`}
          htmlFor={inputId}
        >
          {uploading
            ? "Uploading…"
            : `${value ? "Replace" : "Upload"} ${
                kind === "video" ? "video" : "photo"
              }`}
          <input
            id={inputId}
            type="file"
            accept={types.join(",")}
            disabled={uploading}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file) return;
              if (
                !types.includes(file.type) ||
                file.size > (kind === "video" ? 40 : 5) * 1024 * 1024
              ) {
                setError(
                  kind === "video"
                    ? "Choose an MP4 or WebM video under 40 MB."
                    : "Choose a PNG, JPEG or WebP photo under 5 MB."
                );
                return;
              }
              setError("");
              onUpload(file);
            }}
          />
        </label>
        {value && (
          <button type="button" disabled={uploading} onClick={onRemove}>
            Remove
          </button>
        )}
      </div>
      <small>
        {kind === "video"
          ? "MP4 or WebM · Up to 40 MB"
          : "PNG, JPEG or WebP · Up to 5 MB"}
      </small>
      {error && (
        <p role="alert" className="cms-field-error">
          {error}
        </p>
      )}
    </section>
  );
}
export function ItemFieldsPiece({
  item,
  onChange,
  onUpload,
  uploading,
}: {
  item: MenuItem;
  onChange: (value: MenuItem) => void;
  onUpload: (file: File, sideIndex?: number) => void;
  uploading: boolean;
}) {
  const fields: [keyof MenuItem, string][] = [
    ["title", "Item name"],
    ["price", "Price (USD)"],
    ["description", "Description"],
    ["subtitle", "Included sides heading"],
    ["alt", "Image description"],
    ["link", "Ordering link"],
  ];
  return (
    <>
      <label className="cms-toggle">
        <input
          type="checkbox"
          checked={item.visible}
          onChange={(e) => onChange({ ...item, visible: e.target.checked })}
        />{" "}
        Show on the public menu after publishing
      </label>
      {fields.map(([key, label]) => (
        <TextFieldPiece
          key={key}
          id={`${item.id}-${key}`}
          error={
            key === "title" && !item.title.trim()
              ? "Enter an item name."
              : (key === "img" || key === "link") && !safeUrlFn(item[key])
              ? "Use an HTTPS or site-relative URL."
              : key === "alt" && item.img && !item.alt.trim()
              ? "Describe the image."
              : undefined
          }
          label={label}
          value={item[key] as string}
          multiline={key === "description"}
          onChange={(value) => onChange({ ...item, [key]: value })}
        />
      ))}
      <MediaFieldPiece
        label="Item photo"
        value={item.img}
        uploading={uploading}
        onUpload={onUpload}
        onRemove={() => onChange({ ...item, img: "" })}
      />
      <h3>Included sides</h3>
      {item.includes.map((part, index) => (
        <div key={index}>
          <TextFieldPiece
            label="Side name"
            value={part.item}
            onChange={(value) =>
              onChange({
                ...item,
                includes: item.includes.map((p, i) =>
                  i === index ? { ...p, item: value } : p
                ),
              })
            }
          />
          <MediaFieldPiece
            label="Side photo"
            value={part.img}
            uploading={uploading}
            onUpload={(file) => onUpload(file, index)}
            onRemove={() =>
              onChange({
                ...item,
                includes: item.includes.map((p, i) =>
                  i === index ? { ...p, img: "" } : p
                ),
              })
            }
          />
          <button
            onClick={() =>
              onChange({
                ...item,
                includes: item.includes.filter((_, i) => i !== index),
              })
            }
          >
            Remove side
          </button>
        </div>
      ))}
      <button
        onClick={() =>
          onChange({
            ...item,
            includes: [...item.includes, { item: "", img: "" }],
          })
        }
      >
        Add included side
      </button>
    </>
  );
}
export function BasicFieldsPiece({
  content,
  fields,
  onChange,
  uploading,
  onUpload,
}: {
  content: PageContent;
  fields: [keyof PageContent, string][];
  uploading: boolean;
  onUpload: (file: File, key: keyof PageContent) => void;
  onChange: (value: PageContent) => void;
}) {
  return (
    <>
      {fields.map(([key, label]) =>
        [
          "sowLogo",
          "bkgImg",
          "socialImage",
          "bannerVideoUrl",
          "meetTheOwners",
        ].includes(key) ? (
          <MediaFieldPiece
            key={key}
            label={label.replace(/ URL$/, "")}
            value={content[key] as string}
            kind={
              key === "bannerVideoUrl" || key === "meetTheOwners"
                ? "video"
                : "image"
            }
            uploading={uploading}
            onUpload={(file) => onUpload(file, key)}
            onRemove={() => onChange({ ...content, [key]: "" })}
          />
        ) : (
          <TextFieldPiece
            key={key}
            label={label}
            value={content[key] as string}
            onChange={(value) => onChange({ ...content, [key]: value })}
          />
        )
      )}
    </>
  );
}
